import * as React from 'react'
import { useState, useEffect } from 'react'
import RemoveFieldsButton from '../shared/RemoveFieldsButton'
import { useRemoveShipmentMutation, useShipmentsQuery } from '../../hooks/queries/shipments'
import { ShipmentShape } from '../../api/api_data_types'
import ManualShipmentFields from './ManualShipmentFields'

type ShipmentFieldsProps = {
  jobnumberId: number
  shipmentFields: { [key: string]: ShipmentShape }
  setShipmentFields: (shipmentFields: any) => void
  removeShipment?: (key: string) => void
  setShipment?: (key: string, data: any) => void
  fieldNameFn: (index: number, field: string) => string
  awaitShipmentsCost: boolean
}

export default function ShipmentFields(props: ShipmentFieldsProps) {
  const {
    jobnumberId,
    shipmentFields,
    setShipmentFields,
    removeShipment: removeShipmentFn,
    setShipment,
    fieldNameFn,
    awaitShipmentsCost,
  } = props

  const { data: shipmentsData, isLoading: shipmentsLoading } = useShipmentsQuery(jobnumberId)
  const removeShipmentMutation = useRemoveShipmentMutation()
  const removeShipment = (shipmentId) => {
    removeShipmentMutation.mutate(shipmentId)
  }

  const hasManualWithoutId = Object.values(shipmentFields).some(
    (shipment) => shipment.type === 'Manual' && !shipment.id
  )
  const [showDetails, setShowDetails] = useState(hasManualWithoutId)
  const [awaitCost, setAwaitCost] = useState(awaitShipmentsCost)
  const [bulkSkippedZeroPriceRows, setBulkSkippedZeroPriceRows] = useState(false)
  const shipmentPriceValue = (shipment: ShipmentShape) => {
    const parsedPrice = parseFloat(shipment.price?.toString().replace(',', '.'))
    return Number.isNaN(parsedPrice) ? 0 : parsedPrice
  }
  const bulkEligibleShipments = Object.values(shipmentFields).filter(
    (shipment) => !shipment.approved && !shipment.resources_record_id
  )
  const bulkEligibleZeroPriceCount = bulkEligibleShipments.filter(
    (shipment) => shipmentPriceValue(shipment) === 0
  ).length
  const approveAll = () => {
    const newShipmentFields = { ...shipmentFields }
    let skippedCount = 0
    Object.keys(newShipmentFields).forEach((key) => {
      const shipment = newShipmentFields[key]
      if (shipment.approved || shipment.resources_record_id) {
        return
      }

      if (shipmentPriceValue(shipment) === 0) {
        skippedCount += 1
        return
      }

      shipment.approved = true
    })
    setShipmentFields(newShipmentFields)
    setBulkSkippedZeroPriceRows(skippedCount > 0)
  }
  const approveAvailable = bulkEligibleShipments.length > 0
  const setAwaitCostsAvailable =
    Object.values(shipmentFields).filter((shipment) => shipment.approved == true && !shipment.resources_record_id)
      .length > 0

  useEffect(() => {
    if (shipmentsData) {
      setShipmentFields(shipmentsData)
    }
  }, [shipmentsData])
  useEffect(() => {
    if (hasManualWithoutId && !showDetails) {
      setShowDetails(true)
    }
  }, [hasManualWithoutId])
  useEffect(() => {
    if (bulkSkippedZeroPriceRows && bulkEligibleZeroPriceCount === 0) {
      setBulkSkippedZeroPriceRows(false)
    }
  }, [bulkSkippedZeroPriceRows, bulkEligibleZeroPriceCount])

  const totalShipmentsCount = shipmentFields ? Object.keys(shipmentFields).length : 0
  const totalZeroPriceShipmentsCount = shipmentFields
    ? Object.values(shipmentFields).filter((shipment) => shipmentPriceValue(shipment) === 0).length
    : 0
  const totalZeroPriceLabel = totalZeroPriceShipmentsCount ? ` (${totalZeroPriceShipmentsCount} uden beløb)` : ''
  const shipmentPriceReducer = (acc, shipment) => acc + shipmentPriceValue(shipment)
  const totalPrice = shipmentFields ? Object.values(shipmentFields).reduce(shipmentPriceReducer, 0) : 0
  const skippedShipmentsLabel = bulkEligibleZeroPriceCount === 1 ? 'forsendelse' : 'forsendelser'

  const fieldId = (idKey, field) => `${idKey}-field-${field}`

  const isManualShipment = (shipment: ShipmentShape) => shipment.type === 'Manual'

  const notAllShipmentsApproved = Object.values(shipmentFields).some(
    (shipment) => !shipment.approved && !shipment.resources_record_id
  )
  const showBulkApproveWarning = bulkSkippedZeroPriceRows && bulkEligibleZeroPriceCount > 0

  if (!shipmentFields) {
    return null
  }

  const manualShipmentKeys = Object.keys(shipmentFields)
    .filter((key) => isManualShipment(shipmentFields[key]))
    .reverse()
  const otherShipmentKeys = Object.keys(shipmentFields).filter((key) => !isManualShipment(shipmentFields[key]))
  const shipmentKeys = [...manualShipmentKeys, ...otherShipmentKeys]

  return (
    <fieldset className="resources-time-fields form-fieldset">
      <div className="row">
        <div className="col-sm-8 form-group">
          <label htmlFor={fieldId('shipments-count', 'shipments_count')}>Antal forsendelser</label>
          <input
            type="text"
            id={fieldId('shipments-count', 'shipments_count')}
            required
            className="form-control col-xs-12"
            value={`${totalShipmentsCount} forsendelser${totalZeroPriceLabel}`}
            disabled
          />
        </div>
        <div className="col-sm-4">
          <div className="form-group w-100">
            <label htmlFor={fieldId('total-price', 'total_price')}>Total</label>
            <div className="input-group">
              <input
                id={fieldId('total-price', 'total_price')}
                className="form-control"
                type="number"
                value={totalPrice.toFixed(2)}
                disabled
              />
              <div className="input-group-append">
                <span className="input-group-text">DKK</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 form-group">
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <div className="form-check mr-4">
                <input
                  type="checkbox"
                  id={fieldId('show-details', 'show_details')}
                  className="form-check-input mr-2"
                  checked={showDetails}
                  disabled={hasManualWithoutId}
                  onChange={() => setShowDetails(!showDetails)}
                />
                <label htmlFor={fieldId('show-details', 'show_details')} className="form-check-label">
                  Vis detaljer
                </label>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  id={fieldId('await-cost', 'await_cost')}
                  className="form-check-input mr-2"
                  checked={awaitCost}
                  disabled={!setAwaitCostsAvailable}
                  name="resources_record[await_shipments_cost]"
                  onChange={() => setAwaitCost(!awaitCost)}
                />
                <input type="hidden" name="resources_record[await_shipments_cost]" value={awaitCost ? '1' : '0'} />
                <label htmlFor={fieldId('await-cost', 'await_cost')} className="form-check-label">
                  Afventer omk.
                </label>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!approveAvailable || shipmentsLoading}
              onClick={approveAll}
            >
              Godkend alle
            </button>
          </div>
          {showBulkApproveWarning && (
            <div className="mt-2 text-danger text-right">
              {`${bulkEligibleZeroPriceCount} ${skippedShipmentsLabel} uden beløb blev ikke godkendt. Godkend dem individuelt.`}
            </div>
          )}
          {notAllShipmentsApproved && (
            <div className="mt-2 text-danger text-right">Husk at godkende alle forsendelser</div>
          )}
        </div>
      </div>
      {showDetails &&
        shipmentKeys.map((key, index) => {
          const shipment = shipmentFields[key]
          const id = shipment.id
          const resourcesRecordId = shipment.resources_record_id
          const approved = shipment.approved
          const isDuplicate =
            Object.values(shipmentFields).filter((s) => s.description === shipment.description).length > 1
          const canBeRemoved = !(shipment.approved && shipment.resources_record_id)
          const isManual = isManualShipment(shipment)
          const canBeApproved = id && !resourcesRecordId && !approved

          // Render manual shipment with full edit form (always show for unsaved manual shipments)
          if (isManual && !id) {
            return (
              <ManualShipmentFields
                key={key}
                shipment={shipment}
                onChange={(data) => setShipment(key, data)}
                onRemove={() => removeShipmentFn && removeShipmentFn(key)}
                index={index}
                fieldNameFn={(field) => fieldNameFn(index, field)}
              />
            )
          }

          // Render existing display for Consignor shipments or approved manual shipments
          return (
            <div className="row" key={key}>
              <div className="col-sm-6 form-group">
                {index === 0 && <label htmlFor={fieldId(key, 'description')}>Beskrivelse</label>}
                <input
                  type="text"
                  id={fieldId(key, 'description')}
                  className="form-control col-xs-12"
                  value={shipment.description}
                  disabled
                />
              </div>
              <div className="col-sm-2">
                <div className="form-group w-100">
                  {index === 0 && <label htmlFor={fieldId(key, 'weight')}>Vægt</label>}
                  <input
                    id={fieldId(key, 'weight')}
                    className="form-control"
                    type="text"
                    value={shipment.weight}
                    disabled
                  />
                </div>
              </div>
              <div className="col-sm-2">
                <div className="form-group w-100">
                  {index === 0 && <label htmlFor={fieldId(key, 'price')}>Beløb</label>}
                  <input
                    id={fieldId(key, 'price')}
                    className="form-control"
                    type="text"
                    pattern="[0-9]+([,\.][0-9]+)?"
                    value={shipment.price}
                    onFocus={(e) => e.target.select()}
                    disabled={shipment.approved}
                    onChange={(e) => {
                      setShipment(key, { ...shipment, price: e.target.value })
                    }}
                    name={shipment.approved ? '' : fieldNameFn(index, 'price')}
                  />
                </div>
              </div>
              <div className="col-sm-2 px-2">
                <div className="form-group w-100">
                  {index === 0 && <label>Godkend</label>}
                  <div className="d-flex align-items-center">
                    {approved ? <span className="text-muted mt-2">Godkendt</span> : null}
                    {canBeApproved && (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setShipment && setShipment(key, { ...shipment, approved: true })}
                      >
                        Godkend
                      </button>
                    )}
                    {id && isDuplicate && canBeRemoved && (
                      <RemoveFieldsButton
                        className="ml-2"
                        handleRemove={() => confirm('Er du sikker?') && removeShipment(id)}
                      />
                    )}
                  </div>
                </div>
              </div>
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'id')} value={id} />
              )}
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'approved')} value="1" />
              )}
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'price')} value={shipment.price} />
              )}
            </div>
          )
        })}
      {!showDetails &&
        shipmentKeys.map((key, index) => {
          const shipment = shipmentFields[key]
          const id = shipment.id
          const resourcesRecordId = shipment.resources_record_id
          const approved = shipment.approved

          return (
            <React.Fragment key={key}>
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'id')} value={id} />
              )}
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'approved')} value="1" />
              )}
              {id && !resourcesRecordId && approved && (
                <input type="hidden" name={fieldNameFn(index, 'price')} value={shipment.price} />
              )}
            </React.Fragment>
          )
        })}
    </fieldset>
  )
}
